import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  SLA_DIAGRAM_NOT_FOUND_CODE,
  fetchSlaDefinitions,
  fetchSlaDiagram,
  isSlaFixtureMode,
  processmodelsApi,
  toSlaApiError,
  type SlaApiError,
} from "api";
import { getTranslation } from "common";
import { useBreakpoint, useProcessModels, useTranslations, useUser } from "hooks";
import {
  SLA_FORMAT_RELATIVE,
  SLA_HEALTH_AT_RISK,
  SLA_HEALTH_BREACHED,
  SLA_HEALTH_ON_TIME,
  SLA_TARGET_ACTIVITY,
  type SlaDiagramNode,
  type SlaHealth,
} from "model/slaModel";
import { SLA_DURATION_UNIT_LABEL_KEYS } from "model/slaEditorModel";
import {
  SLA_DIAGRAM_DRILLDOWN_ROUTE,
  slaDiagramConfigureRoute,
  slaDiagramDetailNodes,
  slaDiagramIsNoSlaEventType,
  SLA_DIAGRAM_OTHER_REVISION_LIMIT,
  SLA_DIAGRAM_REMOVED_NODES_LIMIT,
  SLA_DIAGRAM_TEXT_KEYS,
  slaDiagramElementId,
  slaDiagramHealthIcon,
  slaDiagramHealthKey,
  slaDiagramIsStale,
  slaDiagramListNodeTestId,
  slaDiagramNeedsRefetch,
  slaDiagramNodeName,
  slaDiagramNodeRatio,
  slaDiagramNodeRatioPercent,
  slaDiagramNodeRatioValue,
  slaDiagramNodeTestId,
  slaDiagramOldestAsOf,
  slaDiagramRemainingSeconds,
  slaDiagramSummary,
  slaDiagramWorstHealth,
} from "model/slaDiagramModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSlaRuntimeRefresh } from "./useSlaRuntimeRefresh";
import { useNavigate } from "react-router-dom";
import BpmnViewer from "bpmn-js/lib/NavigatedViewer";

/**
 * Superficie **Diagramma SLA** (piano Client sez. 5 F3b, sez. 6 riga 89, sez. 8
 * riga 126).
 *
 * **Canvas riusato, non inventato.** Il canvas e' il `NavigatedViewer` di bpmn-js,
 * lo **stesso** viewer gia' usato da `src/components/dialogs/StartProcess.tsx:373`
 * (controlli di zoom in `:645-700`) e da `Process.tsx:200-215`: `movecanvas` +
 * `zoomscroll` + `keyboard-move` sono moduli del `NavigatedViewer` (`bpmn-js/lib
 * /NavigatedViewer.js`), quindi pan/zoom con mouse, touch e frecce di tastiera
 * arrivano dal modulo riusato. La fonte dell'XML e' la stessa rotta usata da
 * StartProcess (`processmodelsApi.get('/{id}')` -> `data.model`).
 *
 * **Dove sta il dato SLA.** L'endpoint del diagramma
 * (`GET /sla/organizations/{org}/models/{model}/diagram`, contratto letto in
 * `Flowenti-Server/TestServer/Controllers/Controller.SLA.pas:374-405` e
 * `Services/Service.SLA.pas:805-927`) **non** restituisce l'XML: restituisce una
 * riga per nodo, con `designerid` = id BPMN dell'elemento. Il diagramma si compone
 * quindi di due letture indipendenti: l'XML del modello e le statistiche per nodo.
 * Un nodo del payload il cui `designerid` non esiste nel modello corrente resta
 * **elencato** (l'elenco non nasconde nulla) ma non riceve badge sul canvas: e' il
 * rovescio del limite di sez. 11.3 ed e' dichiarato, non aggirato.
 *
 * Responsive (sez. 6 riga 89):
 * - **desktop**: canvas e dettaglio **affiancati** (`md` in su), con zoom e
 *   popover ancorato al nodo;
 * - **tablet**: canvas a piena larghezza con pan/zoom touch (moduli del
 *   `NavigatedViewer`) e tap sui badge;
 * - **mobile**: canvas navigabile e dettaglio raggiungibile **anche dall'elenco**
 *   sotto il canvas; popover/sheet dentro il viewport (MUI `Popover` limita il
 *   paper al viewport, `marginThreshold` di default).
 *
 * Accessibilita': ogni nodo e' un controllo **reale** (`<button>`) sia sul canvas
 * (badge dell'overlay bpmn-js) sia nell'elenco, quindi click/tap/Enter/Spazio
 * aprono lo stesso popover; il popover MUI chiude con Escape e riporta il focus
 * al trigger. Lo stato e' espresso con **colore + forma + testo** (`data-health`,
 * `data-health-icon` e la label dei tre stati del contratto): nessuna informazione
 * vive solo sul colore ne' solo su hover.
 *
 * Aggiornamento runtime (sez. 5 F3d), via `useSlaRuntimeRefresh`: la rilettura
 * dei nodi avviene **al rientro sul tab** (`visibilitychange`/`focus`/`pageshow`)
 * e **a soglia/apertura** (`next_clock_change_at` di un nodo superato,
 * `slaDiagramNeedsRefetch`), e la freschezza (`as_of` contro `nowMs`) e'
 * rivalutata da un orologio che avanza: con dati scaduti la superficie dichiara
 * lo stato e non resta verde. Il refresh non chiude il popover aperto (la
 * selezione non e' azzerata). Nessun canale SSE esiste per il diagramma nel
 * contratto: non se ne inventa uno.
 *
 * Limiti dichiarati, non simulati: `other_revision_instances` conta solo gli
 * stessi `designerid` (`SLA_DIAGRAM_OTHER_REVISION_LIMIT`) e le istanze su nodi
 * rimossi dalla revisione corrente non compaiono affatto
 * (`SLA_DIAGRAM_REMOVED_NODES_LIMIT`, sez. 11.3, decisione 9 del 2026-09-20).
 * Il drill-down apre la lista Monitor (F3a) ma **non** la filtra sul target: la
 * rotta `/instances` non ha quel parametro (`Controller.SLA.pas:309-331`), residuo
 * dichiarato in `model/slaDiagramModel.ts`.
 *
 * Chiavi di traduzione: famiglia `sla.diagram.*` (manifest in
 * `model/slaDiagramModel.ts`), di proprieta' Python P1 e non ancora consegnata:
 * `getTranslation` restituisce la chiave quando il testo manca
 * (`common/utilities.ts:904-905`).
 */
interface SlaDiagramSurfaceProps {
  idorganization: number;
}

interface SlaDiagramModelsResponse {
  model?: string;
}

type SlaDiagramCanvasState = "idle" | "loading" | "ready" | "unavailable";

/** Etichette risolte una volta: i badge dell'overlay sono DOM, non React. */
interface SlaDiagramLabels {
  nodeOpen: string;
  noSla: string;
  noInstances: string;
  hasSla: string;
  health: Record<number, string>;
}

/**
 * Ancoraggio del badge al bordo della forma (overlay bpmn-js, posizione relativa
 * al bounding box): sulle attivita' (rettangoli) il badge sta a cavallo dello
 * spigolo in alto a destra, dentro la larghezza della forma; su eventi (cerchi) e
 * gateway (rombi) lo spigolo del box e' lontano dal tratto, quindi il badge sta
 * centrato sopra il punto piu' alto della forma, a contatto. Lo spostamento
 * fine e' nel CSS (`data-anchor`), perche' la larghezza del badge non e' nota
 * prima della resa.
 */
const badgeAnchor = (element: { type?: string; width?: number }) => {
  const width = typeof element.width === "number" ? element.width : 0;
  const type = element.type ?? "";
  const rounded = /Event$|Gateway$/.test(type);
  return rounded
    ? { anchor: "top", position: { top: 0, left: width / 2 } }
    : { anchor: "corner", position: { top: 0, left: width } };
};

/** Badge sul canvas: forma dello stato, non solo colore (sez. 6 riga 91). */
const HEALTH_GLYPH: Record<string, string> = {
  check: "✓",
  alert: "!",
  error: "✕",
};

/** Marcatore del nodo sul canvas, per lo stato peggiore (worst-state-wins). */
const canvasMarker = (node: SlaDiagramNode, health: number | null): string =>
  health === null
    ? node.has_sla
      ? "sla-diagram-marker-unknown"
      : "sla-diagram-marker-no-sla"
    : `sla-diagram-marker-health-${health}`;

const SlaDiagramSurface = ({ idorganization }: SlaDiagramSurfaceProps) => {
  const translations = useTranslations();
  const user = useUser();
  const navigate = useNavigate();
  const isCompact = useBreakpoint("down", "md");
  const locale = user?.i18n || navigator.language.split("-")[0] || "en";
  const { processModels } = useProcessModels();

  const [modelId, setModelId] = useState<number | null>(null);
  const [revisionDraft, setRevisionDraft] = useState<string>("");
  const [appliedRevision, setAppliedRevision] = useState<number | null>(null);

  const [nodes, setNodes] = useState<SlaDiagramNode[]>([]);
  const [modelRevision, setModelRevision] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<SlaApiError | null>(null);
  const [canvasState, setCanvasState] = useState<SlaDiagramCanvasState>("idle");
  const [overlayCount, setOverlayCount] = useState<number>(0);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  /**
   * Id BPMN degli eventi di inizio/fine del modello disegnato: non portano SLA,
   * quindi niente badge, niente riga nell'elenco e fuori dalla sintesi.
   */
  const [startEndIds, setStartEndIds] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * Durata leggibile dello SLA per `designerid` (definizioni della stessa
   * revisione): la risposta del diagramma ha solo `has_sla`, non la durata.
   */
  const [durationByDesignerId, setDurationByDesignerId] = useState<Record<string, string>>({});

  const [selected, setSelected] = useState<SlaDiagramNode | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
  /**
   * Rilettura runtime (F3d): progressivo nelle dipendenze della lettura dei nodi.
   * Non azzera la selezione, quindi un refresh non chiude il popover aperto.
   */
  const [refreshToken, setRefreshToken] = useState<number>(0);
  const requestRefresh = useCallback(() => {
    setRefreshToken((current) => current + 1);
  }, []);

  /**
   * Refresh a rientro sul tab e a soglia (`next_clock_change_at` superato);
   * `nowMs` alimenta la freschezza senza attendere un'interazione (sez. 5 F3d).
   */
  const { nowMs } = useSlaRuntimeRefresh(
    (at) => slaDiagramNeedsRefetch(nodes, at),
    requestRefresh,
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<any>(null);
  const markerRef = useRef<{ id: string; cls: string }[]>([]);

  /**
   * Etichette risolte per i badge del canvas. `translations` cambia una volta
   * sola, quando il catalogo arriva: i badge si ricostruiscono allora, cosi' non
   * restano con le chiavi grezze.
   */
  const labels: SlaDiagramLabels = useMemo(
    () => ({
      nodeOpen: getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeOpen),
      noSla: getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeNoSla),
      noInstances: getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeNoInstances),
      hasSla: getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeHasSla),
      health: {
        [SLA_HEALTH_ON_TIME]: getTranslation(translations, slaDiagramHealthKey(SLA_HEALTH_ON_TIME)),
        [SLA_HEALTH_AT_RISK]: getTranslation(translations, slaDiagramHealthKey(SLA_HEALTH_AT_RISK)),
        [SLA_HEALTH_BREACHED]: getTranslation(translations, slaDiagramHealthKey(SLA_HEALTH_BREACHED)),
      },
    }),
    [translations],
  );
  const labelsRef = useRef<SlaDiagramLabels>(labels);
  useEffect(() => {
    labelsRef.current = labels;
  }, [labels]);

  // ── Selezione del modello: il diagramma e' model-scoped (sez. 11.2) ────────
  useEffect(() => {
    if (modelId !== null) return;
    if (processModels.length > 0) setModelId(processModels[0].id);
  }, [processModels, modelId]);

  // ── Nodi del diagramma (lettura indipendente dall'XML) ─────────────────────
  useEffect(() => {
    if (!modelId) return;
    let isMounted = true;
    setLoading(true);
    (async () => {
      try {
        const response = await fetchSlaDiagram(
          idorganization,
          modelId,
          appliedRevision && appliedRevision > 0 ? appliedRevision : undefined,
        );
        if (!isMounted) return;
        setNodes(response.diagram ?? []);
        // `meta.model_revision` e' serializzato come **stringa** dal controller
        // (`Controller.SLA.pas:400`): non lo si converte in numero.
        setModelRevision(response.meta?.model_revision ?? "");
        setError(null);
      } catch (caught) {
        if (!isMounted) return;
        // Nessun nodo residuo e nessuno stato precedente: il canvas si svuota e
        // la superficie dichiara l'errore (404 dedicato della rotta incluso).
        setNodes([]);
        setModelRevision("");
        setSelected(null);
        setAnchorEl(undefined);
        setError(toSlaApiError(caught, SLA_DIAGRAM_NOT_FOUND_CODE));
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [idorganization, modelId, appliedRevision, refreshToken]);

  // ── Durate degli SLA per il badge (definizioni della revisione mostrata) ──
  useEffect(() => {
    const revision = Number(modelRevision);
    if (!modelId || !Number.isFinite(revision) || revision <= 0) {
      setDurationByDesignerId({});
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const response = await fetchSlaDefinitions(idorganization, modelId, revision);
        if (!isMounted) return;
        const next: Record<string, string> = {};
        for (const definition of response.definitions ?? []) {
          if (
            definition.targettype !== SLA_TARGET_ACTIVITY ||
            !definition.targetdesignerid ||
            definition.durationformat !== SLA_FORMAT_RELATIVE ||
            definition.durationvalue === null ||
            definition.durationunit === null
          ) {
            continue;
          }
          next[definition.targetdesignerid] = `${definition.durationvalue} ${getTranslation(
            translations,
            SLA_DURATION_UNIT_LABEL_KEYS[definition.durationunit],
          )}`;
        }
        setDurationByDesignerId(next);
      } catch {
        // Definizioni non leggibili (es. 403): il badge ricade su "SLA".
        if (isMounted) setDurationByDesignerId({});
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [idorganization, modelId, modelRevision, translations]);

  // ── Canvas: stesso viewer di StartProcess (pan/zoom mouse, touch, tastiera) ─
  useEffect(() => {
    if (!modelId) return;
    let cancelled = false;
    setCanvasState("loading");
    setStartEndIds(new Set());
    (async () => {
      try {
        const { data } = await processmodelsApi.get<SlaDiagramModelsResponse>(`/${modelId}`);
        if (cancelled) return;
        const xml = data?.model;
        if (!xml) {
          // Modello senza XML: il canvas lo dichiara, l'elenco resta.
          setCanvasState("unavailable");
          return;
        }
        const container = containerRef.current;
        if (!container) {
          setCanvasState("unavailable");
          return;
        }
        container.innerHTML = "";
        const viewer = new BpmnViewer({ container, readOnly: true, additionalModules: [] });
        viewerRef.current = viewer;
        // I marker del viewer precedente non esistono nel nuovo registry.
        markerRef.current = [];
        await viewer.importXML(xml);
        if (cancelled) return;
        (viewer.get("canvas") as any).zoom("fit-viewport");
        setZoomLevel(1);
        setStartEndIds(
          new Set(
            (viewer.get("elementRegistry") as any)
              .filter((element: { type?: string }) => slaDiagramIsNoSlaEventType(element.type))
              .map((element: { id: string }) => element.id),
          ),
        );
        setCanvasState("ready");
      } catch {
        if (!cancelled) setCanvasState("unavailable");
      }
    })();
    return () => {
      cancelled = true;
      if (viewerRef.current) {
        try {
          viewerRef.current.destroy();
        } catch {
          // Il viewer gia' distrutto non e' un errore della superficie.
        }
        viewerRef.current = null;
        markerRef.current = [];
      }
    };
  }, [modelId]);

  const openNode = useCallback((node: SlaDiagramNode, anchor: HTMLElement | undefined) => {
    setSelected(node);
    setAnchorEl(anchor);
  }, []);

  // ── Badge sui nodi: colore + forma + testo, controlli reali ────────────────
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || canvasState !== "ready") {
      setOverlayCount(0);
      return;
    }
    const overlays = viewer.get("overlays") as any;
    const canvas = viewer.get("canvas") as any;
    const registry = viewer.get("elementRegistry") as any;
    if (!overlays || !canvas || !registry) {
      setOverlayCount(0);
      return;
    }
    // Ricostruzione completa: nessun badge di un modello precedente resta.
    overlays.clear();
    // removeMarker vuole la classe; un id assente dal registry farebbe crashare bpmn-js.
    for (const { id, cls } of markerRef.current) {
      if (registry.get(id)) canvas.removeMarker(id, cls);
    }
    markerRef.current = [];

    let placed = 0;
    for (const node of nodes) {
      const elementId = slaDiagramElementId(node);
      if (!elementId) continue;
      const element = registry.get(elementId);
      if (!element) continue;
      // Eventi di inizio/fine: nessuno SLA, nessun badge ne' marcatore.
      if (slaDiagramIsNoSlaEventType(element.type)) continue;

      const health = slaDiagramWorstHealth(node);
      const icon = slaDiagramHealthIcon(health);
      const ratio = slaDiagramNodeRatio(node);
      const currentLabels = labelsRef.current;
      const duration = node.designerid ? durationByDesignerId[node.designerid] : undefined;
      // Testo breve del badge: stato delle istanze aperte se ci sono, altrimenti
      // la durata dello SLA (o "SLA" se la durata non e' leggibile). "Nessuna
      // istanza" resta nel titolo accessibile, non e' un'etichetta dello SLA.
      const stateText =
        health === null
          ? node.has_sla
            ? duration ?? currentLabels.hasSla
            : currentLabels.noSla
          : currentLabels.health[health] ?? String(health);
      const accessibleParts = [stateText];
      if (node.has_sla && health === null) accessibleParts.push(currentLabels.noInstances);
      if (node.has_sla && health !== null && duration) accessibleParts.push(duration);
      const accessible = `${slaDiagramNodeName(node)}: ${accessibleParts.join(" · ")}`;

      const badge = document.createElement("button");
      badge.type = "button";
      badge.className = "sla-diagram-badge";
      badge.setAttribute("data-testid", slaDiagramNodeTestId(node.idnode));
      badge.setAttribute("data-idnode", String(node.idnode));
      badge.setAttribute("data-designerid", node.designerid ?? "");
      badge.setAttribute("data-has-sla", String(node.has_sla));
      badge.setAttribute("data-health", health === null ? "" : String(health));
      const { anchor, position } = badgeAnchor(element);
      badge.setAttribute("data-anchor", anchor);
      badge.setAttribute("data-health-icon", icon ?? "");
      badge.setAttribute("data-other-revision-instances", String(node.other_revision_instances ?? 0));
      badge.setAttribute("data-ratio", slaDiagramNodeRatioValue(ratio));
      badge.setAttribute(
        "data-ratio-percent",
        ratio.percent === null ? "" : String(ratio.percent),
      );
      badge.setAttribute("data-worst-health", node.worst_health === null ? "" : String(node.worst_health));
      badge.setAttribute("aria-label", accessible);
      badge.setAttribute("title", accessible);

      const glyph = document.createElement("span");
      glyph.className = "sla-diagram-badge-glyph";
      glyph.setAttribute("aria-hidden", "true");
      // Forma dello stato: nessuna informazione affidata al solo colore.
      glyph.textContent = icon ? HEALTH_GLYPH[icon] ?? "?" : "–";
      const text = document.createElement("span");
      text.className = "sla-diagram-badge-text";
      text.textContent = stateText;
      badge.appendChild(glyph);
      badge.appendChild(text);

      // Click, tap e tastiera arrivano tutti a `click` (il badge e' un button).
      badge.addEventListener("click", (event) => {
        event.stopPropagation();
        openNode(node, badge);
      });

      overlays.add(elementId, "sla-diagram-health", {
        position,
        html: badge,
      });
      const cls = canvasMarker(node, health);
      canvas.addMarker(elementId, cls);
      markerRef.current.push({ id: elementId, cls });
      placed += 1;
    }
    setOverlayCount(placed);
  }, [nodes, canvasState, openNode, labels, durationByDesignerId]);

  const detailNodes = useMemo(
    () => slaDiagramDetailNodes(nodes, startEndIds),
    [nodes, startEndIds],
  );
  const summary = useMemo(() => slaDiagramSummary(detailNodes), [detailNodes]);
  const asOf = useMemo(() => slaDiagramOldestAsOf(nodes), [nodes]);
  // `as_of` del payload contro l'orologio che avanza: senza il tick lo stato
  // resterebbe verde finche' un'interazione non forza un ricalcolo (sez. 5 F3d).
  const stale = slaDiagramIsStale(nodes, nowMs);

  const applyRevision = () => {
    const parsed = Number(revisionDraft);
    setAppliedRevision(Number.isInteger(parsed) && parsed > 0 ? parsed : null);
    setSelected(null);
    setAnchorEl(undefined);
  };

  const healthChip = (health: SlaHealth | null, part: string) => {
    const icon = slaDiagramHealthIcon(health);
    return (
      <Chip
        size="small"
        icon={
          icon === "check" ? (
            <CheckCircleOutlineIcon />
          ) : icon === "alert" ? (
            <WarningAmberIcon />
          ) : icon === "error" ? (
            <ErrorOutlineIcon />
          ) : (
            <HelpOutlineIcon />
          )
        }
        label={
          health === null
            ? getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeNoInstances)
            : getTranslation(translations, slaDiagramHealthKey(health))
        }
        data-testid={part}
        data-health={health === null ? "" : String(health)}
        data-health-icon={icon ?? ""}
        color={
          health === SLA_HEALTH_ON_TIME
            ? "success"
            : health === SLA_HEALTH_BREACHED
              ? "error"
              : health === SLA_HEALTH_AT_RISK
                ? "warning"
                : "default"
        }
        variant={health === SLA_HEALTH_ON_TIME ? "outlined" : "filled"}
      />
    );
  };

  /** Attributi comuni dei nodi resi nell'elenco (stessi dati del badge). */
  const nodeAttributes = (node: SlaDiagramNode) => {
    const health = slaDiagramWorstHealth(node);
    const ratio = slaDiagramNodeRatio(node);
    return {
      "data-idnode": String(node.idnode),
      "data-designerid": node.designerid ?? "",
      "data-has-sla": String(node.has_sla),
      "data-health": health === null ? "" : String(health),
      "data-health-icon": slaDiagramHealthIcon(health) ?? "",
      "data-worst-health": node.worst_health === null ? "" : String(node.worst_health),
      "data-other-revision-instances": String(node.other_revision_instances ?? 0),
      "data-ratio": slaDiagramNodeRatioValue(ratio),
      "data-ratio-percent": ratio.percent === null ? "" : String(ratio.percent),
    };
  };

  const renderNodeRow = (node: SlaDiagramNode) => {
    const health = slaDiagramWorstHealth(node);
    const ratio = slaDiagramNodeRatio(node);
    return (
      <Box
        key={node.idnode}
        {...nodeAttributes(node)}
        data-testid={slaDiagramListNodeTestId(node.idnode)}
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1,
        }}
      >
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              variant="text"
              data-testid={slaDiagramListNodeTestId(node.idnode, "open")}
              aria-label={`${slaDiagramNodeName(node)}: ${getTranslation(
                translations,
                SLA_DIAGRAM_TEXT_KEYS.nodeOpen,
              )}`}
              onClick={(event) => openNode(node, event.currentTarget)}
            >
              {slaDiagramNodeName(node)}
            </Button>
            {healthChip(health, slaDiagramListNodeTestId(node.idnode, "health"))}
            <Typography variant="caption" color="text.secondary">
              {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeInstances)}
              {": "}
              {String(node.active)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={slaDiagramListNodeTestId(node.idnode, "ratio")}
            >
              {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.ratioLabel)}
              {": "}
              {ratio.available
                ? `${slaDiagramNodeRatioValue(ratio)} (${slaDiagramNodeRatioPercent(ratio, locale)})`
                : getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.ratioNotAvailable)}
            </Typography>
            {!node.has_sla && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={slaDiagramListNodeTestId(node.idnode, "no-sla")}
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeNoSla)}
              </Typography>
            )}
            {node.other_revision_instances > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={slaDiagramListNodeTestId(node.idnode, "other-revisions")}
                data-count={String(node.other_revision_instances)}
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.otherRevisionsLabel)}
                {": "}
                {String(node.other_revision_instances)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const popoverNode = selected;

  return (
    <Box
      id="sla-diagram-shell"
      data-testid="sla-diagram-shell"
      height="100%"
      data-model={modelId === null ? "" : String(modelId)}
      data-model-revision={modelRevision}
      data-nodes={String(nodes.length)}
      data-overlays={String(overlayCount)}
      data-stale={String(stale)}
      data-summary-percent={summary.percent === null ? "" : String(summary.percent)}
      data-summary-without-sla={String(summary.withoutSla)}
      sx={{
        "& .sla-diagram-badge": {
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          minHeight: 20,
          padding: "0 7px",
          borderRadius: 10,
          border: "1px solid",
          // Il badge e' un <button>: senza colore esplicito eredita il nero di
          // sistema, illeggibile sul fondo scuro.
          borderColor: "primary.main",
          backgroundColor: "background.paper",
          color: "text.primary",
          font: "inherit",
          fontSize: "0.7rem",
          fontWeight: 600,
          lineHeight: 1.2,
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: 1,
        },
        // Ancoraggio al bordo della forma (vedi `badgeAnchor`).
        "& .sla-diagram-badge[data-anchor='corner']": { transform: "translate(-100%, -50%)" },
        "& .sla-diagram-badge[data-anchor='top']": {
          transform: "translate(-50%, calc(-100% - 2px))",
        },
        "& .sla-diagram-badge-glyph": { fontWeight: 700 },
        // Coppie fondo/testo della palette: contrasto garantito dal tema in
        // chiaro e in scuro (`*.contrastText`).
        "& .sla-diagram-badge[data-health='0']": {
          borderColor: "success.dark",
          backgroundColor: "success.main",
          color: "success.contrastText",
        },
        "& .sla-diagram-badge[data-health='1']": {
          borderColor: "warning.dark",
          backgroundColor: "warning.main",
          color: "warning.contrastText",
        },
        "& .sla-diagram-badge[data-health='2']": {
          borderColor: "error.dark",
          backgroundColor: "error.main",
          color: "error.contrastText",
        },
        "& .sla-diagram-badge[data-has-sla='false']": {
          borderStyle: "dashed",
          borderColor: "text.secondary",
          backgroundColor: "background.paper",
          color: "text.secondary",
          fontWeight: 500,
          boxShadow: "none",
        },
        // Stato peggiore sul nodo del canvas: colore **e** bordo, cosi' non e' il
        // solo colore a distinguere (sez. 6 riga 91).
        "& .sla-diagram-marker-health-0 .djs-visual > :first-of-type": {
          stroke: "#2e7d32",
          strokeWidth: 3,
        },
        "& .sla-diagram-marker-health-1 .djs-visual > :first-of-type": {
          stroke: "#ed6c02",
          strokeWidth: 3,
        },
        "& .sla-diagram-marker-health-2 .djs-visual > :first-of-type": {
          stroke: "#d32f2f",
          strokeWidth: 3,
        },
        "& .sla-diagram-marker-no-sla .djs-visual > :first-of-type": {
          strokeDasharray: "4 2",
        },
        "& .sla-diagram-marker-unknown .djs-visual > :first-of-type": { opacity: 0.75 },
      }}
    >
      <Stack spacing={2} sx={{ pb: 2 }}>
        <Stack
          direction={isCompact ? "column" : "row"}
          spacing={2}
          alignItems={isCompact ? "stretch" : "center"}
          justifyContent="space-between"
        >
          <Typography variant="h6">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.title)}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              select
              size="small"
              label={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.modelLabel)}
              value={modelId === null ? "" : String(modelId)}
              onChange={(event) => {
                setModelId(Number(event.target.value));
                setSelected(null);
                setAnchorEl(undefined);
              }}
              SelectProps={{ inputProps: { "data-testid": "sla-diagram-model" } }}
              sx={{ minWidth: 200 }}
            >
              {processModels.length === 0 && (
                <MenuItem value="" disabled>
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.modelNone)}
                </MenuItem>
              )}
              {processModels.map((model) => (
                <MenuItem key={model.id} value={String(model.id)}>
                  {model.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.revisionLabel)}
              value={revisionDraft}
              onChange={(event) => setRevisionDraft(event.target.value)}
              onBlur={applyRevision}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyRevision();
              }}
              inputProps={{ inputMode: "numeric", "data-testid": "sla-diagram-revision" }}
              placeholder={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.revisionAuto)}
              sx={{ width: 160 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="sla-diagram-model-revision"
            >
              {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.revisionApplied)}
              {": "}
              {modelRevision || "—"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="sla-diagram-as-of"
            >
              {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.asOf)}
              {": "}
              {asOf ? slaDiagramDateTimeLabel(asOf) : "—"}
            </Typography>
          </Stack>
        </Stack>

        {isSlaFixtureMode() && (
          <Alert severity="info">
            {getTranslation(translations, "sla.editor.fixture_notice")}
          </Alert>
        )}

        {stale && (
          <Alert severity="warning" data-testid="sla-diagram-stale">
            {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.stale)}
          </Alert>
        )}

        {error && (
          <Alert
            severity="error"
            data-testid="sla-diagram-error"
            data-error-code={error.code ?? ""}
            data-error-status={error.status === null ? "" : String(error.status)}
            data-error-message-key={
              error.code === SLA_DIAGRAM_NOT_FOUND_CODE
                ? SLA_DIAGRAM_TEXT_KEYS.modelNotFound
                : error.status === 403
                  ? SLA_DIAGRAM_TEXT_KEYS.forbidden
                  : SLA_DIAGRAM_TEXT_KEYS.loadError
            }
          >
            {getTranslation(
              translations,
              error.code === SLA_DIAGRAM_NOT_FOUND_CODE
                ? SLA_DIAGRAM_TEXT_KEYS.modelNotFound
                : error.status === 403
                  ? SLA_DIAGRAM_TEXT_KEYS.forbidden
                  : SLA_DIAGRAM_TEXT_KEYS.loadError,
            )}
          </Alert>
        )}

        {/* Sintesi: i nodi senza SLA sono contati e il rapporto e' N/D a
            denominatore zero, mai 0%. */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="baseline"
          flexWrap="wrap"
          data-testid="sla-diagram-summary"
          data-nodes={String(summary.nodes)}
          data-with-sla={String(summary.withSla)}
          data-without-sla={String(summary.withoutSla)}
          data-active={String(summary.active)}
          data-problematic={String(summary.problematic)}
          data-percent={summary.percent === null ? "" : String(summary.percent)}
          data-worst={summary.worst === null ? "" : String(summary.worst)}
        >
          <Typography variant="caption" color="text.secondary">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.summaryLabel)}
          </Typography>
          <Typography variant="caption" data-testid="sla-diagram-summary-nodes">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.summaryNodes)}
            {": "}
            {String(summary.nodes)}
          </Typography>
          <Typography variant="caption" data-testid="sla-diagram-summary-without-sla">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.summaryWithoutSla)}
            {": "}
            {String(summary.withoutSla)}
          </Typography>
          <Typography variant="caption" data-testid="sla-diagram-summary-active">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.summaryActive)}
            {": "}
            {String(summary.active)}
          </Typography>
          <Typography variant="caption" data-testid="sla-diagram-summary-ratio">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.ratioLabel)}
            {": "}
            {summary.percent === null
              ? getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.ratioNotAvailable)
              : `${summary.problematic}/${summary.active} (${slaDiagramNodeRatioPercent(
                  { problematic: summary.problematic, total: summary.active, percent: summary.percent, available: true },
                  locale,
                )})`}
          </Typography>
        </Stack>

        <Divider />

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={20} data-testid="sla-diagram-loading" />
            <Typography variant="caption" color="text.secondary">
              {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.loading)}
            </Typography>
          </Stack>
        )}

        {!loading && !error && nodes.length === 0 && (
          <Alert severity="info" data-testid="sla-diagram-empty">
            {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.empty)}
          </Alert>
        )}

        {/* Il canvas esiste da subito: il contenitore del viewer deve esserci
            quando la lettura dell'XML si risolve, altrimenti il disegno non
            avverrebbe mai (nessuna corsa fra le due letture). */}
        <Stack spacing={1} data-testid="sla-diagram-detail-zone">
            <Stack
              direction={isCompact ? "column" : "row"}
              spacing={2}
              alignItems="stretch"
            >
              {/* Canvas: viewer bpmn-js riusato (pan/zoom mouse, touch, tastiera). */}
              <Box
                data-testid="sla-diagram-canvas"
                data-canvas-state={canvasState}
                aria-label={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.canvasLabel)}
                sx={{
                  position: "relative",
                  flex: "1 1 auto",
                  minWidth: 0,
                  height: isCompact ? 320 : 460,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "hidden",
                  backgroundColor: "background.paper",
                  touchAction: "none",
                }}
              >
                <Box ref={containerRef} sx={{ width: "100%", height: "100%" }} />
                {canvasState !== "ready" && canvasState !== "loading" && (
                  <Stack
                    sx={{ position: "absolute", inset: 0, p: 2 }}
                    alignItems="center"
                    justifyContent="center"
                    data-testid="sla-diagram-canvas-unavailable"
                    data-canvas-reason={modelId === null ? "no_model" : "no_xml"}
                  >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {getTranslation(
                        translations,
                        // Senza modello scelto non c'e' nulla da disegnare; con il
                        // modello scelto ma senza XML il canvas lo dichiara.
                        modelId === null
                          ? SLA_DIAGRAM_TEXT_KEYS.modelNone
                          : SLA_DIAGRAM_TEXT_KEYS.canvasUnavailable,
                      )}
                    </Typography>
                  </Stack>
                )}
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 5,
                    px: 0.5,
                    py: 0.25,
                  }}
                >
                  <IconButton
                    size="small"
                    data-testid="sla-diagram-zoom-out"
                    aria-label={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.zoomOut)}
                    onClick={() => {
                      const next = Math.max(0.2, Number((zoomLevel - 0.15).toFixed(2)));
                      viewerRef.current?.get("canvas").zoom(next);
                      setZoomLevel(next);
                    }}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                  <Button
                    size="small"
                    data-testid="sla-diagram-zoom-fit"
                    aria-label={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.zoomFit)}
                    onClick={() => {
                      viewerRef.current?.get("canvas").zoom("fit-viewport");
                      setZoomLevel(1);
                    }}
                  >
                    <FitScreenIcon fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {`${Math.round(zoomLevel * 100)}%`}
                    </Typography>
                  </Button>
                  <IconButton
                    size="small"
                    data-testid="sla-diagram-zoom-in"
                    aria-label={getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.zoomIn)}
                    onClick={() => {
                      const next = Math.min(3, Number((zoomLevel + 0.15).toFixed(2)));
                      viewerRef.current?.get("canvas").zoom(next);
                      setZoomLevel(next);
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              {/* Dettaglio: affiancato da `md` in su, sotto su compatto; resta
                  raggiungibile anche senza canvas (sez. 6 riga 89). */}
              <Stack
                spacing={1}
                data-testid="sla-diagram-nodes"
                sx={
                  isCompact
                    ? { width: "100%" }
                    : { width: 360, flexShrink: 0, maxHeight: 460, overflowY: "auto" }
                }
              >
                <Typography variant="subtitle2">
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodesTitle)}
                </Typography>
                {detailNodes.map(renderNodeRow)}
              </Stack>
            </Stack>

            {/* Limiti dichiarati, non aggirati (sez. 11.3, decisione 9). */}
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="sla-diagram-other-revisions-limit"
                data-limit={SLA_DIAGRAM_OTHER_REVISION_LIMIT}
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.otherRevisionsLimit)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="sla-diagram-removed-nodes-limit"
                data-limit={SLA_DIAGRAM_REMOVED_NODES_LIMIT}
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.removedNodesLimit)}
              </Typography>
            </Stack>
        </Stack>
      </Stack>

      {/* Popover: click/tap/tastiera lo aprono, Escape lo chiude e MUI riporta il
          focus al trigger (il Paper resta dentro il viewport). */}
      <Popover
        open={Boolean(popoverNode) && Boolean(anchorEl)}
        anchorEl={anchorEl ?? undefined}
        onClose={() => {
          setSelected(null);
          setAnchorEl(undefined);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { maxWidth: "min(360px, 92vw)", p: 0 } }}
      >
        {popoverNode && (
          <Box data-testid="sla-diagram-popover" sx={{ p: 2, maxWidth: 360 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  data-testid="sla-diagram-popover-name"
                  sx={{ wordBreak: "break-word" }}
                >
                  {slaDiagramNodeName(popoverNode)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.detailTitle)}
                </Typography>
              </Stack>
              <IconButton
                size="small"
                data-testid="sla-diagram-popover-close"
                aria-label={getTranslation(translations, "generic.button.cancel")}
                onClick={() => {
                  setSelected(null);
                  setAnchorEl(undefined);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Stack spacing={1}>
              {healthChip(
                slaDiagramWorstHealth(popoverNode),
                "sla-diagram-popover-health",
              )}

              {!popoverNode.has_sla && (
                <Typography
                  variant="caption"
                  data-testid="sla-diagram-popover-no-sla"
                  data-has-sla="false"
                >
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeNoSla)}
                </Typography>
              )}

              {(() => {
                const ratio = slaDiagramNodeRatio(popoverNode);
                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="baseline"
                    data-testid="sla-diagram-popover-ratio"
                    data-ratio={slaDiagramNodeRatioValue(ratio)}
                    data-ratio-percent={ratio.percent === null ? "" : String(ratio.percent)}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.ratioLabel)}
                      {": "}
                    </Typography>
                    <Typography variant="caption">
                      {ratio.available
                        ? `${slaDiagramNodeRatioValue(ratio)} (${slaDiagramNodeRatioPercent(
                            ratio,
                            locale,
                          )})`
                        : getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.ratioNotAvailable)}
                    </Typography>
                  </Stack>
                );
              })()}

              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                data-testid="sla-diagram-popover-instances"
                data-active={String(popoverNode.active)}
              >
                <Typography variant="caption" color="text.secondary">
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.nodeInstances)}
                  {": "}
                </Typography>
                <Typography variant="caption">{String(popoverNode.active)}</Typography>
              </Stack>

              {/* Istanza piu' urgente: secondi gia' calcolati dal Server. */}
              {slaDiagramRemainingSeconds(popoverNode) !== null && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="baseline"
                  data-testid="sla-diagram-popover-remaining"
                  data-remaining-working-seconds={String(
                    slaDiagramRemainingSeconds(popoverNode),
                  )}
                >
                  <Typography variant="caption" color="text.secondary">
                    {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.timezone)}
                    {": "}
                  </Typography>
                  <Typography variant="caption">
                    {slaDiagramDateTimeLabel(popoverNode.next_deadline_at) || "—"}
                  </Typography>
                </Stack>
              )}

              {/* Revisioni precedenti: contate, mai fuse con lo stato del nodo. */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                data-testid="sla-diagram-popover-other-revisions"
                data-count={String(popoverNode.other_revision_instances ?? 0)}
              >
                <Typography variant="caption" color="text.secondary">
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.otherRevisionsLabel)}
                  {": "}
                </Typography>
                <Typography variant="caption">
                  {String(popoverNode.other_revision_instances ?? 0)}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="sla-diagram-popover-other-revisions-limit"
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.otherRevisionsLimit)}
              </Typography>

              {/* Drill-down: riusa la superficie F3a, nessuna lista nuova. */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                data-testid="sla-diagram-popover-drilldown"
                onClick={() => {
                  setSelected(null);
                  setAnchorEl(undefined);
                  navigate(SLA_DIAGRAM_DRILLDOWN_ROUTE);
                }}
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.drillDown)}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="sla-diagram-popover-drilldown-hint"
              >
                {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.drillDownHint)}
              </Typography>

              {/* Configurazione SLA del nodo nel designer del modello. */}
              {modelId !== null && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<SettingsOutlinedIcon />}
                  data-testid="sla-diagram-popover-configure"
                  onClick={() => {
                    setSelected(null);
                    setAnchorEl(undefined);
                    navigate(slaDiagramConfigureRoute(modelId, popoverNode.designerid));
                  }}
                >
                  {getTranslation(translations, SLA_DIAGRAM_TEXT_KEYS.configure)}
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

/**
 * Istante ISO reso in UTC con suffisso esplicito. Il diagramma **non** porta una
 * timezone per riga (`SlaDiagramNode` non ha `timezone_id`): si rende la base UTC
 * dichiarata, **mai** quella del browser (sez. 4).
 */
function slaDiagramDateTimeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const iso = parsed.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}Z`;
}

export default SlaDiagramSurface;
